import { ChangeDetectionStrategy, Component, inject, output, signal } from '@angular/core';
import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import { TsButtonModule, TsDividerComponent, TSIconModule, TSTreeMenuModule } from 'tsui';
import { map, tap } from 'rxjs';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import SavedViewService from '@services/saved-view.service';
import { RelatedToType } from '@shared/enums';
import { TaskListService } from '@tasks/services/task-list.service';
import { AccountState } from '@state/account/state';
import { Store } from '@ngxs/store';
import { Router, RouterModule } from '@angular/router';
import { TaskSidebarConfigService } from '@tasks/services/task-sidebar-config.service';
import { SavedViewAddComponent } from '@shared/modules/saved-views/modals/saved-view-add/saved-view-add.component';
import { TaskRelatedToModule } from '@tasks/tasks.types';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { SavedView } from '@core/models';
import { UserState } from '@state/user/state';
import { NewPermission } from '@core/enums';

@Component({
  selector: 'app-shell-task-sidebar',
  standalone: true,
  imports: [
    TSIconModule,
    TsButtonModule,
    NgTemplateOutlet,
    AsyncPipe,
    MatProgressSpinnerModule,
    RouterModule,
    TSTreeMenuModule,
    TsDividerComponent,
  ],
  providers: [TaskSidebarConfigService],
  templateUrl: './shell-task-sidebar.component.html',
  styleUrl: './shell-task-sidebar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ShellTaskSidebarComponent {
  private readonly router = inject(Router);
  private readonly savedViewsService = inject(SavedViewService);
  private readonly taskListService = inject(TaskListService);
  private readonly store = inject(Store);
  private readonly taskSidebarConfigService = inject(TaskSidebarConfigService);
  private readonly matDialog = inject(MatDialog);

  canCreateGlobal$ = this.store.select(UserState.hasPermission(NewPermission.GLOBAL_TASK_LISTS_CREATE));
  canCreateAggregated$ = this.store.select(UserState.hasPermission(NewPermission.AGGREGATED_TASK_LISTS_CREATE));

  onNavigate = output<void>();
  isLoading = signal({ aggregatedTasks: true, globalTasks: true });
  account = this.store.selectSnapshot(AccountState.getAccount);
  savedViews = signal<SavedView[]>([]);
  aggregatedTasksViews$ = this.savedViewsService.get(RelatedToType.AggregatedTasks).pipe(
    map((response) => {
      this.savedViews.set(response);

      return (response ?? []).map((view) => ({
        id: String(view.id),
        name: view.name,
        icon: 'ts_list-ul',
        link: '/new-tasks/AggregatedTasks/' + this.account.id + '/' + view.id,
      }));
    }),
    tap(() => this.isLoading.set({ ...this.isLoading(), aggregatedTasks: false })),
  );
  globalTasksViews$ = this.taskListService
    .getGlobalTaskLists(+this.store.selectSnapshot(AccountState.getAccount)?.id)
    .pipe(
      map((response) => {
        const sorted = [...(response ?? [])].sort((a, b) => (a.rank ?? 0) - (b.rank ?? 0));
        return sorted.map((view) => ({
          id: String(view.id),
          name: view.name,
          icon: 'ts_list-ul',
          link: '/new-tasks/Account/' + this.account.id + '/' + view.id,
        }));
      }),
      tap(() => this.isLoading.set({ ...this.isLoading(), globalTasks: false })),
    );

  delegateLeafClickFn(route: string): void {
    this.router.navigateByUrl(route);
    this.onNavigate.emit();
  }

  openNewViewDialog(type: 'aggregated' | 'global'): void {
    if (type === 'aggregated') {
      this.matDialog.open(SavedViewAddComponent, {
        panelClass: 'mailbox-compose-dialog',
        data: {
          relatedToType: TaskRelatedToModule.AggregatedTasks,
          saved_views: this.savedViews(),
        },
      });
    } else {
      this.taskSidebarConfigService.openCreateTaskListDialog(TaskRelatedToModule.Account);
    }
  }
}
